import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import { Link } from "react-router-dom";

const MaterialInvoiceTable = ({
  materialOrders,
  categories,
  inventoryData,
  handleCategoryChange,
  handleMaterialChange,
  handleAddMaterial,
  handleRemoveMaterial,
  taxRates,
  handleMaterialTaxChange,
  dealerId,
  pricingData, 
}) => {
  const [currentCategories, setCurrentCategories] = useState([]);
  const [showPriceNotification, setShowPriceNotification] = useState(false);
  const [priceEditLink, setPriceEditLink] = useState("");

  useEffect(() => {
    const categories = materialOrders.map((order) => order.category || "");
    setCurrentCategories(categories);
  }, [materialOrders]);

  const handleMaterialSelection = (materialIndex, value) => {
    if (!dealerId || pricingData.length === 0) {
      // If no dealer is selected or no pricing data, allow manual entry
      handleMaterialChange(materialIndex, "material", value);
      handleMaterialChange(materialIndex, "sellingPrice", 0);
      handleMaterialChange(materialIndex, "discountRate", 0);
      handleMaterialChange(materialIndex, "customCost", "");
      return;
    }

    // Dealer pricing logic
    handleMaterialChange(materialIndex, "material", value);
    const pricingItem = pricingData.find((item) => item.inventoryItem === value);

    if (!pricingItem || pricingItem.price === 0) {
      setPriceEditLink(`/dealer/edit-material-rate/${dealerId}`);
      setShowPriceNotification(true);
      handleMaterialChange(materialIndex, "customCost", 0);
      handleMaterialChange(materialIndex, "sellingPrice", 0);
      handleMaterialChange(materialIndex, "discountRate", 0);
    } else {
      const inventoryItem = inventoryData.find((item) => item._id === value);
      const sellingPrice = inventoryItem?.sellingPrice || 0;
      const quantity = materialOrders[materialIndex].quantity || 1;
      const unitPrice =
        pricingItem.pricingType === "discount"
          ? sellingPrice * (1 - pricingItem.price / 100)
          : pricingItem.price;

      handleMaterialChange(
        materialIndex,
        "customCost",
        Number((unitPrice * quantity).toFixed(2))
      );
      handleMaterialChange(
        materialIndex,
        "sellingPrice",
        Number(sellingPrice.toFixed(2))
      );
      handleMaterialChange(
        materialIndex,
        "discountRate",
        pricingItem.pricingType === "discount"
          ? Number(pricingItem.price.toFixed(2))
          : 0
      );
    }
  };

  const handleQuantityChange = (materialIndex, value) => {
    const quantity = value ? parseFloat(value) : 0;
    handleMaterialChange(materialIndex, "quantity", quantity);

    if (!dealerId || pricingData.length === 0) {
      return; // If no dealer, custom cost remains manual
    }

    const material = materialOrders[materialIndex];
    if (material.material) {
      const pricingItem = pricingData.find(
        (item) => item.inventoryItem === material.material
      );

      if (pricingItem && pricingItem.price !== 0) {
        const inventoryItem = inventoryData.find(
          (item) => item._id === material.material
        );
        const sellingPrice = inventoryItem?.sellingPrice || 0;
        const unitPrice =
          pricingItem.pricingType === "discount"
            ? sellingPrice * (1 - pricingItem.price / 100)
            : pricingItem.price;

        handleMaterialChange(
          materialIndex,
          "customCost",
          Number((unitPrice * quantity).toFixed(2))
        );
        handleMaterialChange(
          materialIndex,
          "sellingPrice",
          Number(sellingPrice.toFixed(2))
        );
        handleMaterialChange(
          materialIndex,
          "discountRate",
          pricingItem.pricingType === "discount"
            ? Number(pricingItem.price.toFixed(2))
            : 0
        );
      } else {
        handleMaterialChange(materialIndex, "customCost", 0);
        handleMaterialChange(materialIndex, "sellingPrice", 0);
        handleMaterialChange(materialIndex, "discountRate", 0);
      }
    }
  };

  const handleCategorySelection = (materialIndex, categoryValue) => {
    handleCategoryChange(materialIndex, categoryValue);
    const updatedCategories = [...currentCategories];
    updatedCategories[materialIndex] = categoryValue;
    setCurrentCategories(updatedCategories);
  };

  const pricingDescription =
    pricingData.length > 0
      ? pricingData[0].pricingType === "discount"
        ? "The discount will be reduced from the inventory selling price."
        : "The fixed price set in the dealer material pricing is used as material pricing."
      : dealerId
      ? "No pricing data available for this dealer. Please set pricing for materials."
      : "Custom company: Enter pricing details manually.";

  return (
    <div className="col-lg-12 mt-3">
      <h5>Material Orders</h5>
      <div className="mb-3">
        <p>{pricingDescription}</p>
      </div>
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Category</th>
            <th>Material</th>
            <th>Quantity</th>
            <th>Selling Price ($)</th>
            <th>Discount Rate (%)</th>
            <th>Custom Cost ($)</th>
            <th>Tax Rates</th>
            <th>Tax ($)</th>
            <th>Total ($)</th>
            <th>Remove</th>
          </tr>
        </thead>
        <tbody>
          {materialOrders.map((material, materialIndex) => {
            const categoryDisplay = categories?.find(
              (cat) => cat.value === material.category
            )?.name || material.category || "";

            return (
              <tr key={materialIndex}>
                <td>
                  <select
                    className="form-select"
                    value={material.category || ""}
                    onChange={(e) =>
                      handleCategorySelection(materialIndex, e.target.value)
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="form-select"
                    value={material.material || ""}
                    onChange={(e) =>
                      handleMaterialSelection(materialIndex, e.target.value)
                    }
                    disabled={!material.category}
                  >
                    <option value="">Select Material</option>
                    {material.category &&
                      inventoryData
                        .filter((item) => {
                          const inventoryCategory = item.category;
                          return inventoryCategory === material.category;
                        })
                        .map((item) => (
                          <option key={item._id} value={item._id}>
                            {item.name}
                          </option>
                        ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={material.quantity || ""}
                    onChange={(e) =>
                      handleQuantityChange(materialIndex, e.target.value)
                    }
                    min="0"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={material.sellingPrice || 0}
                    onChange={(e) =>
                      handleMaterialChange(
                        materialIndex,
                        "sellingPrice",
                        e.target.value
                      )
                    }
                    min="0"
                    step="0.01"
                    readOnly={!!dealerId && pricingData.length > 0}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={material.discountRate || 0}
                    onChange={(e) =>
                      handleMaterialChange(
                        materialIndex,
                        "discountRate",
                        e.target.value
                      )
                    }
                    min="0"
                    step="0.01"
                    readOnly={!!dealerId && pricingData.length > 0}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={material.customCost || ""}
                    onChange={(e) =>
                      handleMaterialChange(
                        materialIndex,
                        "customCost",
                        e.target.value
                      )
                    }
                    min="0"
                    step="0.01"
                  />
                </td>
                <td>
                  {taxRates.map((taxRate) => (
                    <div
                      key={taxRate._id}
                      className="d-flex align-items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={material.selectedTaxRates?.some(
                          (rate) => rate._id === taxRate._id
                        )}
                        onChange={(e) =>
                          handleMaterialTaxChange(
                            materialIndex,
                            taxRate,
                            e.target.checked
                          )
                        }
                      />
                      <label className="form-check-label mb-0">
                        {taxRate.type} ({taxRate.value}%)
                      </label>
                    </div>
                  ))}
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={material.tax?.toFixed(2) || 0}
                    disabled
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={material.total?.toFixed(2) || 0}
                    disabled
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleRemoveMaterial(materialIndex)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        type="button"
        className="btn btn-primary mt-3"
        onClick={handleAddMaterial}
      >
        Add Material
      </button>

      <Dialog
        open={showPriceNotification}
        onClose={() => setShowPriceNotification(false)}
      >
        <DialogTitle>Price Not Set</DialogTitle>
        <DialogContent>
          <p>
            The price for the selected material is not set. Please set the price
            first.
          </p>
          <p>
            <Link to={priceEditLink} rel="noopener noreferrer">
              Click here to set the price
            </Link>
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowPriceNotification(false)}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MaterialInvoiceTable;